import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { BookOpen, User, Home, Search, ChevronRight, Sparkles, Clock } from 'lucide-react';

function CatedrasList() {
  const [catedras, setCatedras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchCatedras = async () => {
      try {
        const { data } = await api.fetchPublicCatedras();
        setCatedras(data || []);
      } catch (err) {
        setError('Error al cargar las cátedras.');
        console.error('Error fetching public catedras:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCatedras();
  }, []);

  const filteredCatedras = catedras.filter((c) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    const docenteName = `${c.Docente?.nombre || c.docente?.nombre || ''} ${c.Docente?.apellido || c.docente?.apellido || ''}`.toLowerCase();
    return (
      (c.nombre || '').toLowerCase().includes(q) ||
      (c.institucion || '').toLowerCase().includes(q) ||
      String(c.anio || '').includes(q) ||
      docenteName.includes(q)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500/30 border-t-emerald-400 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-300 text-lg font-medium">Cargando cátedras disponibles...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-red-500/40 p-8 text-center">
          <p className="text-red-400 text-lg font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-10 px-4 sm:px-6 lg:px-8 text-white">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Header y Buscador */}
        <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 sm:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-semibold mb-2">
              <Sparkles size={14} />
              Espacio Académico Abierto
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-white">
              Cátedras Disponibles
            </h1>
            <p className="text-slate-400 mt-1">
              Consulta los planes de estudio, actividades, docentes y seguimiento académico de cada cátedra.
            </p>
          </div>

          <div className="relative w-full md:w-80">
            <Search size={18} className="absolute left-3.5 top-3 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar cátedra, docente o año..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800/90 border border-slate-700 text-white text-sm focus:outline-none focus:border-emerald-400"
            />
          </div>
        </div>

        {filteredCatedras.length === 0 ? (
          <div className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-12 text-center">
            <BookOpen className="w-16 h-16 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-300 text-lg font-medium">No se encontraron cátedras disponibles.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCatedras.map((catedra) => {
              const docente = catedra.Docente || catedra.docente;
              return (
                <Link
                  key={catedra.id}
                  to={`/public/catedras/${catedra.id}`}
                  className="group bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/60 hover:border-emerald-400/60 p-6 flex flex-col justify-between transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-emerald-500/10"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-lg shadow-emerald-900/30">
                        <BookOpen size={24} />
                      </div>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                        Año {catedra.anio}
                      </span>
                    </div>

                    <h2 className="text-xl font-bold text-white mb-3 group-hover:text-emerald-300 transition-colors">
                      {catedra.nombre}
                    </h2>

                    <div className="space-y-2 text-sm text-slate-300">
                      {docente && (
                        <div className="flex items-center gap-2 text-slate-300">
                          <User size={15} className="text-blue-400 flex-shrink-0" />
                          <span>Prof. {docente.nombre} {docente.apellido}</span>
                        </div>
                      )}
                      {catedra.institucion && (
                        <div className="flex items-center gap-2 text-slate-400">
                          <Home size={15} className="text-purple-400 flex-shrink-0" />
                          <span className="truncate">{catedra.institucion}</span>
                        </div>
                      )}
                      {catedra.dias && (
                        <div className="flex items-center gap-2 text-slate-400">
                          <Clock size={15} className="text-amber-400 flex-shrink-0" />
                          <span className="truncate">{catedra.dias} {catedra.turno ? `(${catedra.turno})` : ''}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-slate-800 flex items-center justify-between text-emerald-400 text-sm font-semibold">
                    <span>Ver detalles de la cátedra</span>
                    <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default CatedrasList;
